import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Divider } from '@mui/material';
import { Stack } from '@mui/system';

import CustomerDetailsPanel from '@/shared/components/panels/CustomerDetailsPanel';
import WaveUINotificationBar from '@/shared/components/utilities/WaveUINotificationBar';
import WaveUIPageLoader from '@/shared/components/utilities/WaveUIPageLoader';
import AgentNotes from '@/shared/components/widgets/AgentNotes/AgentNotes';
import useAgentNotes from '@/shared/components/widgets/AgentNotes/hooks/useAgentNotes';
import WaveUIAccordion from '@/shared/components/widgets/WaveUIAccordion';
import useNotificationBar from '@/shared/hooks/useNotificationBar';

import AccountDetailsForm from './components/AccountDetailsForm';
import BillingAddressForm from './components/BillingAddressForm';
import DeliveryAddressForm from './components/DeliveryAddressForm';
import OrdersPanel from './components/OrdersPanel';
import PaymentsPanel from './components/PaymentsPanel';
import Title from './components/Title';
import useAccountDetailsForm from './hooks/useAccountDetailsForm';
import useBillingAddressForm from './hooks/useBillingAddressForm';
import useCustomerEditData from './hooks/useCustomerEditData';
import useCustomerEditOperations from './hooks/useCustomerEditOperations';
import useDeliveryAddressForm from './hooks/useDeliveryAddressForm';

const CustomerEdit: FC = () => {
  const customerId = useParams().id || '';
  const { t } = useTranslation(['customers', 'forms']);

  const { notificationBar, onUpdateNotificationBar } = useNotificationBar();

  const { customer, orders, payments } = useCustomerEditData({ customerId });

  const {
    accountDetailsFormFields,
    accountDetailsFormErrors,
    onUpdateAccountDetails,
    onValidateAccountDetails,
  } = useAccountDetailsForm({ customer });

  const { billingAddressFormFields, onUpdateBillingAddress } =
    useBillingAddressForm({ customer });

  const { deliveryAddressFormFields, onUpdateDeliveryAddress } =
    useDeliveryAddressForm({ customer });

  const { saveInProgress, handleSaveCustomer } = useCustomerEditOperations({
    customerId,
    onUpdateNotificationBar,
  });

  const agentNotesState = useAgentNotes({
    customerId,
    module: 'customers',
    moduleItemId: customerId,
  });

  if (!customer || !orders || !payments) {
    return <WaveUIPageLoader />;
  }

  return (
    <div>
      <Title
        customerId={customerId}
        isLoading={saveInProgress}
        onSaveCustomer={() => {
          const isCustomerDetailsValid = onValidateAccountDetails();

          if (isCustomerDetailsValid) {
            void handleSaveCustomer({
              accountDetailsFormFields,
              billingAddressFormFields,
              deliveryAddressFormFields,
            });
          }
        }}
      />

      <Stack
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(300px, 1fr))',
          gap: 2,
        }}
      >
        <Stack spacing={2}>
          {customer && (
            <>
              <CustomerDetailsPanel customer={customer} />

              <AccountDetailsForm
                accountDetailsFormFields={accountDetailsFormFields}
                accountDetailsFormErrors={accountDetailsFormErrors}
                onUpdateAccountDetails={onUpdateAccountDetails}
              />

              <WaveUIAccordion title={t('customers:addresses')} defaultExpanded>
                <DeliveryAddressForm
                  deliveryAddressFormFields={deliveryAddressFormFields}
                  onUpdateDeliveryAddress={onUpdateDeliveryAddress}
                />

                {!deliveryAddressFormFields.billing_same_as_delivery && (
                  <>
                    <Divider sx={{ py: 0, mx: -2 }} />
                    <BillingAddressForm
                      billingAddressFormFields={billingAddressFormFields}
                      onUpdateBillingAddress={onUpdateBillingAddress}
                    />
                  </>
                )}
              </WaveUIAccordion>
            </>
          )}
        </Stack>

        <Stack spacing={2}>
          {orders && <OrdersPanel orders={orders} customerId={customerId} />}
          {payments && <PaymentsPanel payments={payments} />}
        </Stack>

        <div>
          <AgentNotes title='Notes' agentNotesState={agentNotesState} />
        </div>
      </Stack>

      <WaveUINotificationBar
        message={notificationBar.message}
        severity={notificationBar.severity}
        open={notificationBar.open}
        close={() => onUpdateNotificationBar({ open: false })}
      />
    </div>
  );
};

export default CustomerEdit;
